import {
  componentSizeLabel,
  ComponentPosition
} from 'components/equipment-page/component-size-label';
import {
  DuctedSwapBadge,
  DuctedSwapBadgeKind,
  DuctedSwapSlot
} from './ducted-component-swap-types';

type SpecRow = Record<string, unknown> | undefined;

type BadgeBuilder = {
  kind: DuctedSwapBadgeKind;
  build: (spec: SpecRow, combination: Record<string, unknown>) => string | null;
};

const SLOT_POSITIONS: Record<DuctedSwapSlot, ComponentPosition> = {
  outdoor: 'Outdoor',
  indoor: 'Indoor',
  blower: 'Blower'
};

const REFRIGERANT_TEXT_COLUMNS = [
  'PrimaryRefrigerant',
  'Refrigerant',
  'RefrigerantCompatibility'
];
const REFRIGERANT_BIT_COLUMNS = ['R-454B', 'R-410A', 'R-32', 'R-22'];
const ORIENTATION_COLUMNS = ['Upflow', 'Downflow', 'Horizontal'];

const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const readFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === '1' || value === 'true';

const formatRating = (value: number): string =>
  String(Math.round(value * 10) / 10);

const ratingBadgeFor = (
  spec: SpecRow,
  combination: Record<string, unknown>,
  column: string,
  unit: string
): string | null => {
  const value = readNumber(spec?.[column]) ?? readNumber(combination[column]);
  return value === null ? null : `${formatRating(value)} ${unit}`;
};

const sizeBadgeLabel = (
  slot: DuctedSwapSlot,
  spec: SpecRow,
  combination: Record<string, unknown>
): string | null => {
  const size = componentSizeLabel(SLOT_POSITIONS[slot], spec, combination);
  return size === null ? null : `${size} BTU`;
};

const seer2Badge: BadgeBuilder = {
  kind: 'seer2',
  build: (spec, combination) =>
    ratingBadgeFor(spec, combination, 'SEER2', 'SEER2')
};

const hspf2Badge: BadgeBuilder = {
  kind: 'hspf2',
  build: (spec, combination) =>
    ratingBadgeFor(spec, combination, 'HSPF2', 'HSPF2')
};

const afueBadge: BadgeBuilder = {
  kind: 'afue',
  build: (spec, combination) => {
    const value = readNumber(spec?.AFUE) ?? readNumber(combination.AFUE);
    return value === null ? null : `${formatRating(value)}% AFUE`;
  }
};

const stagesBadge: BadgeBuilder = {
  kind: 'stages',
  build: (spec) => {
    const value = readText(spec?.Stages);
    if (value === null) return null;
    return /stage/i.test(value) ? value : `${value} Stage`;
  }
};

const fuelBadge: BadgeBuilder = {
  kind: 'fuel',
  build: (spec) => readText(spec?.FuelType)
};

const cabinetBadge: BadgeBuilder = {
  kind: 'cabinet',
  build: (spec) => {
    const value = readText(spec?.CabinetChassisSize);
    return value === null ? null : `${value} Cabinet`;
  }
};

const refrigerantBadge: BadgeBuilder = {
  kind: 'refrigerant',
  build: (spec) => {
    if (!spec) return null;
    const text = REFRIGERANT_TEXT_COLUMNS.map((column) =>
      readText(spec[column])
    ).find((value) => value !== null);
    if (text) {
      return text
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== '')
        .join(' / ');
    }
    const flagged = REFRIGERANT_BIT_COLUMNS.filter((column) =>
      readFlag(spec[column])
    );
    return flagged.length === 0 ? null : flagged.join(' / ');
  }
};

const orientationBadge: BadgeBuilder = {
  kind: 'orientation',
  build: (spec) => {
    if (!spec) return null;
    const present = ORIENTATION_COLUMNS.filter((column) =>
      readFlag(spec[column])
    );
    if (present.length === 0) return null;
    if (present.length === ORIENTATION_COLUMNS.length) return 'Multi-Position';
    return present.join('/');
  }
};

const SPEC_BADGE_BUILDERS: Record<DuctedSwapSlot, BadgeBuilder[]> = {
  outdoor: [seer2Badge, hspf2Badge, stagesBadge, refrigerantBadge],
  indoor: [cabinetBadge, orientationBadge, refrigerantBadge],
  blower: [afueBadge, fuelBadge, stagesBadge, orientationBadge]
};

export function ductedSwapBadges(
  slot: DuctedSwapSlot,
  spec: SpecRow,
  combination: Record<string, unknown>
): DuctedSwapBadge[] {
  const sizeLabel = sizeBadgeLabel(slot, spec, combination);
  const sizeBadges: DuctedSwapBadge[] =
    sizeLabel === null ? [] : [{ kind: 'size', label: sizeLabel }];
  const specBadges = SPEC_BADGE_BUILDERS[slot].flatMap(({ kind, build }) => {
    const label = build(spec, combination);
    return label === null ? [] : [{ kind, label }];
  });
  return [...sizeBadges, ...specBadges];
}

const badgeLabel = (
  badges: DuctedSwapBadge[],
  kind: DuctedSwapBadgeKind
): string => badges.find((badge) => badge.kind === kind)?.label ?? '';

export function distinguishingBadgeKinds(
  optionBadges: DuctedSwapBadge[][],
  currentBadges: DuctedSwapBadge[] | null
): Set<DuctedSwapBadgeKind> {
  const kinds: DuctedSwapBadgeKind[] = [];
  optionBadges.flat().forEach((badge) => {
    if (!kinds.includes(badge.kind)) kinds.push(badge.kind);
  });
  const compared = currentBadges
    ? [...optionBadges, currentBadges]
    : optionBadges;
  const distinguishing = kinds.filter(
    (kind) =>
      new Set(compared.map((badges) => badgeLabel(badges, kind))).size > 1
  );
  if (distinguishing.length > 0 || kinds.length === 0) {
    return new Set(distinguishing);
  }
  return new Set([kinds.includes('size') ? 'size' : kinds[0]]);
}
